import React from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import GettingStartedMiniCard from './GettingStartedMiniCard';
import useGettingStartedData from './useGettingStartedData';

function ProgressDonut({ percent = 0, size = 42, stroke = 5 }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(100, Math.max(0, percent));
  const dash = (progress / 100) * circumference;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth={stroke} />
      <circle
        cx={size/2}
        cy={size/2}
        r={radius}
        fill="none"
        stroke="#ffffff"
        strokeWidth={stroke}
        strokeDasharray={`${dash} ${circumference - dash}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`}
      />
      <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" fontSize="16" fill="#ffffff" fontWeight="700">
        {progress}%
      </text>
    </svg>
  );
}

export default function GettingStartedDrawer({ open = false, onClose = () => {}, current_user = {}, company = {}, features = [] }) {
  const intl = useIntl();
  const { activities, isActivityCompleted, percent, completed, total } = useGettingStartedData(current_user, company, features, { source: 'drawer' }, intl);

  React.useEffect(() => {
    try {
      const storeUser = (typeof document !== 'undefined' && document.store?.getState?.()?.user) || {};
      const payload = {
        propsUserId: current_user?.id,
        propsUserName: current_user?.name,
        storeUserId: storeUser?.id,
        storeUserName: storeUser?.name,
      };
      // eslint-disable-next-line no-console
      console.log('[GettingStartedDrawer] user ids', payload);
      if (typeof window !== 'undefined') window.__drawerUserDebug = payload;
    } catch (_) {}
  }, [current_user?.id]);

  const container = React.useMemo(() => {
    const el = document.createElement('div');
    el.setAttribute('data-getting-started-drawer', '');
    return el;
  }, []);

  React.useEffect(() => {
    document.body.appendChild(container);
    return () => { try { document.body.removeChild(container); } catch (_) {} };
  }, [container]);

  if (!open) return null;

  const content = (
    <div className="fixed inset-0" aria-modal="true" role="dialog" style={{ zIndex: 99999999998 }}>
      <div className="absolute inset-0" onClick={onClose} />
      {/* External close button to the left of drawer */}
      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute top-3 p-2 rounded-full bg-succession-bg-primary border border-succession-border shadow-sm text-succession-text-secondary hover:bg-succession-bg-secondary"
        style={{ right: 'calc(min(33vw, 420px) + 8px)', zIndex: 99999999999 }}
      >
        ✕
      </button>

      <div className="absolute right-0 top-0 h-full bg-succession-bg-primary shadow-2xl border-l border-succession-border flex flex-col text-succession-text-primary" style={{ width: 'min(33vw, 420px)', zIndex: 99999999999 }}>
        {/* Gradient banner card */}
        <div className="px-3 pt-3">
          <div className="relative rounded-xl p-4 text-white shadow"
               style={{ backgroundImage: 'linear-gradient(135deg, var(--succession-primary, #5489B6), var(--succession-secondary, #002949))' }}>
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">
                  {intl.formatMessage({ id: 'gettingStarted.drawer.greeting' }, { name: String(current_user.name || current_user.user_name || 'there').split(' ')[0] })}
                </div>
                <div className="text-xs mt-1 opacity-90">{intl.formatMessage({ id: 'gettingStarted.drawer.tasks_completed' }, { completed, total })}</div>
              </div>
              <div className="shrink-0">
                <ProgressDonut percent={percent} size={63} stroke={7} />
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 overflow-auto flex-1 bg-succession-bg-primary">
          <div className="grid grid-cols-1 gap-3">
            {(activities || []).map((a) => (
              <GettingStartedMiniCard key={a.id} activity={a} completed={isActivityCompleted(a.id)} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, container);
}


